import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  clientPortalApi,
  ClientPortalTicketAttachmentInput,
  ClientPortalTicketInput,
} from '../../services/api';
import { useClientPortalAuth } from '../../context/ClientPortalAuthContext';
import { useToast } from '../../components/Toast';
import {
  ClientPortalProfile,
  ClientPortalTicket,
  ClientPortalTicketAttachment,
  SupportTicketMessage,
} from '../../types';
import './ClientPortal.css';

const EMPTY_TICKET_FORM: ClientPortalTicketInput = {
  subject: '',
  message_text: '',
  attachments: [],
};

const TICKET_FILTERS = ['Tous', 'En cours', 'Traite', 'Archive'];
const MAX_TICKET_ATTACHMENTS = 5;
const MAX_TICKET_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const TICKET_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.txt',
  '.zip',
  '.rar',
];
const TICKET_ATTACHMENT_ACCEPT = TICKET_ATTACHMENT_EXTENSIONS.join(',');

function formatDateTimeLabel(value?: string | null): string {
  if (!value) {
    return '--';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function getStatusClassName(status: string): string {
  const normalized = status.toLowerCase();
  if (normalized === 'traite') {
    return 'done';
  }
  if (normalized === 'archive') {
    return 'archived';
  }
  return 'progress';
}

function getClientTicketMessageAuthorLabel(
  message: Pick<SupportTicketMessage, 'sender_type' | 'staff_name'>
): string {
  if (message.sender_type === 'client') {
    return 'Vous';
  }

  return message.staff_name || 'Equipe support';
}

function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex >= 0 ? fileName.slice(lastDotIndex).toLowerCase() : '';
}

function formatFileSize(size: number): string {
  if (size < 1024) {
    return `${size} o`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} Ko`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const markerIndex = result.indexOf(',');
      if (markerIndex === -1) {
        reject(new Error('Encodage du fichier impossible.'));
        return;
      }

      resolve(result.slice(markerIndex + 1));
    };

    reader.onerror = () => reject(new Error(`Lecture du fichier "${file.name}" impossible.`));
    reader.readAsDataURL(file);
  });
}

function notifyNotificationSummaryChanged() {
  window.dispatchEvent(new Event('client-portal-notifications-changed'));
}

const ClientPortalTickets: React.FC = () => {
  const { user } = useClientPortalAuth();
  const { showToast } = useToast();
  const ticketAttachmentInputRef = useRef<HTMLInputElement | null>(null);

  const [profile, setProfile] = useState<ClientPortalProfile | null>(null);
  const [tickets, setTickets] = useState<ClientPortalTicket[]>([]);
  const [ticketForm, setTicketForm] = useState<ClientPortalTicketInput>(EMPTY_TICKET_FORM);
  const [ticketFilter, setTicketFilter] = useState('Tous');
  const [loading, setLoading] = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [savingTicket, setSavingTicket] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<number | null>(null);
  const [showTicketComposer, setShowTicketComposer] = useState(false);

  useEffect(() => {
    if (user?.must_change_password) {
      setLoading(false);
      return;
    }

    let mounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const profileResponse = await clientPortalApi.getProfile();
        const profileData = profileResponse.data as ClientPortalProfile;

        if (!mounted) {
          return;
        }

        setProfile(profileData);

        setLoadingTickets(true);
        try {
          const ticketsResponse = await clientPortalApi.getTickets();
          if (mounted) {
            setTickets(Array.isArray(ticketsResponse.data) ? ticketsResponse.data : []);
            notifyNotificationSummaryChanged();
          }
        } catch (ticketError: any) {
          if (mounted) {
            showToast(
              ticketError.response?.data?.error ||
                'Chargement de l historique des tickets impossible.',
              'error'
            );
          }
        } finally {
          if (mounted) {
            setLoadingTickets(false);
          }
        }
      } catch (err: any) {
        if (mounted) {
          showToast(err.response?.data?.error || 'Chargement des tickets impossible.', 'error');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      mounted = false;
    };
  }, [showToast, user?.must_change_password]);

  const filteredTickets =
    ticketFilter === 'Tous'
      ? tickets
      : tickets.filter((ticket) => ticket.status === ticketFilter);

  const canSubmitTicket = Boolean(profile?.email?.trim());

  const handleTicketAttachmentSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = '';

    if (selectedFiles.length === 0) {
      return;
    }

    const existingAttachments = ticketForm.attachments || [];
    const remainingSlots = MAX_TICKET_ATTACHMENTS - existingAttachments.length;

    if (remainingSlots <= 0) {
      showToast(`Maximum ${MAX_TICKET_ATTACHMENTS} pieces jointes par ticket.`, 'warning');
      return;
    }

    const filesToRead = selectedFiles.slice(0, remainingSlots);
    const skippedReasons: string[] = [];

    if (selectedFiles.length > filesToRead.length) {
      skippedReasons.push(`maximum ${MAX_TICKET_ATTACHMENTS} fichiers atteint`);
    }

    const validFiles = filesToRead.filter((file) => {
      const extension = getFileExtension(file.name);
      if (!TICKET_ATTACHMENT_EXTENSIONS.includes(extension)) {
        skippedReasons.push(`${file.name}: format non autorise`);
        return false;
      }

      if (file.size > MAX_TICKET_ATTACHMENT_BYTES) {
        skippedReasons.push(`${file.name}: fichier trop volumineux`);
        return false;
      }

      return true;
    });

    try {
      const encodedAttachments = await Promise.all(
        validFiles.map(async (file): Promise<ClientPortalTicketAttachmentInput> => ({
          file_name: file.name,
          content_type: file.type || 'application/octet-stream',
          file_size: file.size,
          base64_content: await readFileAsBase64(file),
        }))
      );

      setTicketForm((prev) => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...encodedAttachments],
      }));

      if (skippedReasons.length > 0) {
        showToast(skippedReasons.join(' | '), 'warning');
      }
    } catch (error: any) {
      showToast(error.message || 'Ajout des pieces jointes impossible.', 'error');
    }
  };

  const handleRemoveTicketAttachment = (indexToRemove: number) => {
    setTicketForm((prev) => ({
      ...prev,
      attachments: (prev.attachments || []).filter((_, index) => index !== indexToRemove),
    }));
  };

  const handleCloseTicketComposer = () => {
    setShowTicketComposer(false);
    setTicketForm(EMPTY_TICKET_FORM);
    if (ticketAttachmentInputRef.current) {
      ticketAttachmentInputRef.current.value = '';
    }
  };

  const handleDownloadAttachment = async (
    ticketId: number,
    attachment: ClientPortalTicketAttachment
  ) => {
    setDownloadingAttachmentId(attachment.id);
    try {
      const response = await clientPortalApi.downloadTicketAttachment(ticketId, attachment.id);
      const blob = new Blob([response.data], {
        type: attachment.content_type || 'application/octet-stream',
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err: any) {
      showToast(
        err.response?.data?.error || 'Telechargement de la piece jointe impossible.',
        'error'
      );
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const handleCreateTicket = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!ticketForm.subject.trim() || !ticketForm.message_text.trim()) {
      showToast('Le sujet et le message du ticket sont obligatoires.', 'warning');
      return;
    }

    setSavingTicket(true);
    try {
      const response = await clientPortalApi.createTicket(ticketForm);
      const createdTicket: ClientPortalTicket = {
        id: response.data.ticket_id,
        subject: ticketForm.subject.trim(),
        message_text: ticketForm.message_text.trim(),
        status: response.data.status || 'En cours',
        client_email: profile?.email || '',
        created_at: response.data.created_at,
        updated_at: response.data.created_at,
        unread_reply_count: 0,
        attachments: Array.isArray(response.data.attachments) ? response.data.attachments : [],
        messages: [],
      };

      setTickets((prev) => [createdTicket, ...prev]);
      setTicketForm(EMPTY_TICKET_FORM);
      setShowTicketComposer(false);
      if (ticketAttachmentInputRef.current) {
        ticketAttachmentInputRef.current.value = '';
      }

      if (response.data.warning) {
        showToast(response.data.warning, 'warning');
      } else {
        showToast('Ticket depose avec succes.', 'success');
      }
      notifyNotificationSummaryChanged();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Soumission du ticket impossible.', 'error');
    } finally {
      setSavingTicket(false);
    }
  };

  if (user?.must_change_password) {
    return <Navigate to="/client/profile" replace />;
  }

  if (loading) {
    return <div className="loading">Chargement des tickets...</div>;
  }

  return (
    <div className="client-portal-stack">
      <section className="client-portal-card">
        <div className="client-portal-card-header">
          <div>
            <h2>Tickets support</h2>
            <p>Suivez vos demandes support et consultez les reponses de l equipe.</p>
          </div>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              if (showTicketComposer) {
                handleCloseTicketComposer();
              } else {
                setShowTicketComposer(true);
              }
            }}
            disabled={!canSubmitTicket}
          >
            {showTicketComposer ? 'Fermer le formulaire' : 'Soumettre un ticket'}
          </button>
        </div>

        {!canSubmitTicket && (
          <div className="client-portal-warning">
            Un email valide est obligatoire sur votre profil avant de pouvoir soumettre un
            ticket.
          </div>
        )}

        {showTicketComposer && (
          <form className="client-portal-ticket-composer" onSubmit={handleCreateTicket}>
            <div className="form-group">
              <label>Sujet du ticket</label>
              <input
                type="text"
                value={ticketForm.subject}
                onChange={(e) =>
                  setTicketForm((prev) => ({ ...prev, subject: e.target.value }))
                }
                placeholder="Ex: Probleme d activation WINALCO PAIE"
              />
            </div>

            <div className="form-group">
              <label>Message</label>
              <textarea
                rows={6}
                value={ticketForm.message_text}
                onChange={(e) =>
                  setTicketForm((prev) => ({ ...prev, message_text: e.target.value }))
                }
                placeholder="Decrivez votre demande ou l incident rencontre."
              />
            </div>

            <div className="form-group">
              <label>Pieces jointes</label>
              <input
                ref={ticketAttachmentInputRef}
                type="file"
                multiple
                accept={TICKET_ATTACHMENT_ACCEPT}
                onChange={handleTicketAttachmentSelection}
              />
              <small className="client-portal-field-help">
                Jusqu a {MAX_TICKET_ATTACHMENTS} fichiers. Taille maximale {formatFileSize(
                  MAX_TICKET_ATTACHMENT_BYTES
                )} par fichier.
              </small>
            </div>

            {(ticketForm.attachments || []).length > 0 && (
              <div className="client-portal-ticket-attachments-editor">
                {(ticketForm.attachments || []).map((attachment, index) => (
                  <div
                    key={`${attachment.file_name}-${attachment.file_size}-${index}`}
                    className="client-portal-ticket-attachment-chip"
                  >
                    <div>
                      <strong>{attachment.file_name}</strong>
                      <span>{formatFileSize(attachment.file_size)}</span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleRemoveTicketAttachment(index)}
                      disabled={savingTicket}
                    >
                      Retirer
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="client-portal-form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseTicketComposer}
                disabled={savingTicket}
              >
                Annuler
              </button>
              <button type="submit" className="btn btn-primary" disabled={savingTicket}>
                {savingTicket ? 'Envoi...' : 'Envoyer le ticket'}
              </button>
            </div>
          </form>
        )}

        <div className="client-portal-ticket-filters">
          {TICKET_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`client-portal-filter${ticketFilter === filter ? ' active' : ''}`}
              onClick={() => setTicketFilter(filter)}
            >
              {filter}
            </button>
          ))}
        </div>

        {loadingTickets ? (
          <div className="loading">Chargement des tickets...</div>
        ) : filteredTickets.length === 0 ? (
          <div className="client-portal-empty">
            Aucun ticket {ticketFilter === 'Tous' ? '' : `avec le statut "${ticketFilter}" `}a afficher.
          </div>
        ) : (
          <div className="client-portal-ticket-list">
            {filteredTickets.map((ticket) => (
              <article key={ticket.id} className="client-portal-ticket-item">
                <div className="client-portal-ticket-top">
                  <div>
                    <strong className="client-portal-ticket-subject">{ticket.subject}</strong>
                    <div className="client-portal-ticket-meta">
                      Ticket #{ticket.id} · {formatDateTimeLabel(ticket.created_at)}
                    </div>
                  </div>

                  <div className="client-portal-ticket-top-side">
                    <span
                      className={`client-portal-ticket-status ${getStatusClassName(ticket.status)}`}
                    >
                      {ticket.status}
                    </span>
                    {Boolean(ticket.unread_reply_count) && (
                      <span className="client-portal-ticket-unread-badge">
                        {ticket.unread_reply_count} nouveau(x)
                      </span>
                    )}
                  </div>
                </div>

                <div className="client-portal-thread-list">
                  {[
                    {
                      id: 0,
                      sender_type: 'client' as const,
                      staff_id: null,
                      staff_name: null,
                      message_text: ticket.message_text,
                      read_at: null,
                      created_at: ticket.created_at,
                      updated_at: ticket.created_at,
                    },
                    ...(ticket.messages || []),
                  ].map((message, index) => (
                    <div
                      key={`${message.sender_type}-${message.id}-${index}`}
                      className={`client-portal-thread-item ${
                        message.sender_type === 'staff' ? 'staff' : 'client'
                      }`}
                    >
                      <div className="client-portal-thread-meta">
                        <strong>{getClientTicketMessageAuthorLabel(message)}</strong>
                        <span>{formatDateTimeLabel(message.created_at)}</span>
                      </div>
                      <p className="client-portal-ticket-message">{message.message_text}</p>
                    </div>
                  ))}
                </div>

                {(ticket.attachments || []).length > 0 && (
                  <div className="client-portal-ticket-attachments">
                    <span className="client-portal-ticket-attachments-label">
                      Pieces jointes
                    </span>
                    <div className="client-portal-ticket-attachments-list">
                      {(ticket.attachments || []).map((attachment) => (
                        <button
                          key={attachment.id}
                          type="button"
                          className="client-portal-ticket-download"
                          onClick={() => handleDownloadAttachment(ticket.id, attachment)}
                          disabled={downloadingAttachmentId === attachment.id}
                        >
                          <span>{attachment.file_name}</span>
                          <small>{formatFileSize(attachment.file_size)}</small>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ClientPortalTickets;
